<template>
  <div ref="otpCont">
    <KInput
      type="text"
      class="otp"
      v-for="(el, ind) in digits"
      :key="el+ind"
      v-model="digits[ind]"
      :autofocus="ind === 0"
      maxlength="1"
      inputmode="numeric"
      @keydown="handleKeyDown($event, ind)"
      @input="handleInput($event, ind)"
      @paste="handlePaste($event, ind)"
      :class="{bounce: digits[ind] !== null}"
      :error="error"
    />
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import KInput from '@components/inputs/input.vue';

const props = defineProps({
  default: String,

  digitCount: {
    type: Number,
    default: 5,
  },

  error: {
    type: Boolean,
    default: false,
  },
});

const digits = reactive([]);

if (props.default && props.default.length === props.digitCount) {
  for (let i = 0; i < props.digitCount; i += 1) {
    digits[i] = props.default.charAt(i);
  }
} else {
  for (let i = 0; i < props.digitCount; i += 1) {
    digits[i] = null;
  }
}

const otpCont = ref(null);

const emit = defineEmits(['input', 'complete']);

let lastEmittedOtp = null;

const checkComplete = () => {
  const otp = digits.join('');
  if (otp.length === props.digitCount && !digits.includes(null)) {
    if (otp !== lastEmittedOtp) {
      lastEmittedOtp = otp;
      emit('complete', otp);
    }
  } else {
    lastEmittedOtp = null;
  }
};

const handleKeyDown = (event, index) => {
  if (event.key !== 'Tab'
    && event.key !== 'ArrowRight'
    && event.key !== 'ArrowLeft'
    && !event.ctrlKey
    && !event.metaKey
  ) {
    event.preventDefault();
  }

  if (event.key === 'Backspace') {
    digits[index] = null;

    if (index !== 0) {
      (otpCont.value.children)[index - 1].getElementsByTagName('input')[0].focus();
    }
  } else if ((/^([0-9])$/).test(event.key)) {
    digits[index] = event.key;

    if (index !== props.digitCount - 1) {
      (otpCont.value.children)[index + 1].getElementsByTagName('input')[0].focus();
    }
  } else if (event.key !== 'Enter') {
    return;
  }

  emit('input', digits.join(''));
  checkComplete();
};

const handleInput = (value, index) => {
  // Fires on mobile where keydown's event.preventDefault() doesn't block the input event.
  // On desktop, preventDefault stops character insertion so this never fires for normal typing.
  const digit = typeof value === 'string' ? value.replace(/\D/g, '').charAt(0) : '';
  if (digit) {
    digits[index] = digit;
    if (index !== props.digitCount - 1) {
      (otpCont.value.children)[index + 1].getElementsByTagName('input')[0].focus();
    }
  } else {
    digits[index] = null;
  }
  emit('input', digits.join(''));
  checkComplete();
};

const handlePaste = (event, index) => {
  event.preventDefault();
  const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, props.digitCount - index);

  for (let i = 0; i < pasted.length; i += 1) {
    digits[index + i] = pasted.charAt(i);
  }

  const nextIndex = Math.min(index + pasted.length, props.digitCount - 1);
  (otpCont.value.children)[nextIndex].getElementsByTagName('input')[0].focus();

  emit('input', digits.join(''));
  checkComplete();
};
</script>

<style scoped lang="scss">
.otp {
  width: calc(var(--grid-unit) * 5);
  display: inline-block;
  margin: 0 calc(var(--grid-unit) * .5);

  @media screen and (min-width: 768px) {
    margin: 0 calc(var(--grid-unit) * 1.5);
  }

  :deep(.k-input) {
    height: 42px;
    display: inline-block;
  }
}
</style>
